import { useEffect, useRef } from 'react'
import { useLifecycles } from 'react-use'

import { styled } from '@mui/material'
import { LineChart as EChartsLineChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'

const ChartContainer = styled('div')`
  width: 100%;
  height: 333px;
`

export type LineChartProps<XData, SeriesData> = {
  tooltip?: {
    valueFormatter?: (value: any) => string
  }
  grid?: {
    top?: number | string
    bottom?: number | string
    left?: number | string
    right?: number | string
    containLabel?: boolean
    width?: number | string
  }
  xAxis?: {
    name?: string
    data?: XData[]
  }
  yAxis?: {
    name?: string
    axisLabelFormatter?: string | ((value: any, index: number) => string)
  }
  series: {
    name: string
    data?: SeriesData[]
  }
  loading?: boolean
}

export const LineChart = <XData, SeriesData>(props: LineChartProps<XData, SeriesData>) => {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const echartsInstanceRef = useRef<echarts.ECharts>()

  const gridConfig: LineChartProps<XData, SeriesData>['grid'] = {
    left: '5',
    right: '5',
    bottom: '0%',
    width: 'auto',
    containLabel: true,
  }
  if (props.grid && props.grid.top) {
    gridConfig.top = props.grid.top
  }
  if (props.grid && props.grid.bottom) {
    gridConfig.bottom = props.grid.bottom
  }
  if (props.grid && props.grid.left) {
    gridConfig.left = props.grid.left
  }
  if (props.grid && props.grid.right) {
    gridConfig.right = props.grid.right
  }
  if (props.grid && props.grid.containLabel) {
    gridConfig.containLabel = props.grid.containLabel
  }

  useLifecycles(
    () => {
      echarts.use([EChartsLineChart, SVGRenderer, GridComponent, TooltipComponent, LegendComponent])
      echartsInstanceRef.current = echarts.init(chartContainerRef.current)
    },
    () => {
      if (echartsInstanceRef.current) {
        echartsInstanceRef.current.dispose()
      }
    }
  )

  useEffect(() => {
    if (!chartContainerRef.current || !echartsInstanceRef.current) {
      return
    }

    echartsInstanceRef.current.setOption({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: props.tooltip?.valueFormatter,
      },
      grid: gridConfig,
      legend: {},
      color: ['#f45b41'],
      xAxis: [
        {
          type: 'category',
          name: props.xAxis?.name,
          data: props.xAxis?.data,
          boundaryGap: false,
          splitLine: {
            show: true,
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: props.yAxis?.name,
          nameLocation: 'end',
          boundaryGap: false,
          splitLine: {
            show: true,
          },
          axisLabel: {
            formatter: props.yAxis?.axisLabelFormatter,
          },
        },
      ],
      series: [
        {
          type: 'line',
          name: props.series.name,
          data: props.series.data,
          smooth: true,
          areaStyle: {
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            opacity: 0.5,
            color: '#9d9d9d4e',
          },
        },
      ],
    })

    if (props.loading) {
      echartsInstanceRef.current.showLoading()
    } else {
      echartsInstanceRef.current.hideLoading()
    }
  })

  return (
    <>
      <ChartContainer ref={chartContainerRef} />
    </>
  )
}
